﻿import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useAuth, SignInButton, SignUpButton } from "@clerk/react"
import { useEffect, useRef, useState, type RefObject } from "react"
import { MimirLogo } from "@/components/mimir-logo"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import LightRays from "@/components/light-rays"

export const Route = createFileRoute("/")({
  component: Landing,
})

// ---------------------------------------------------------------------------
// Scroll reveal
// ---------------------------------------------------------------------------
function useReveal(threshold = 0.1): { ref: RefObject<HTMLDivElement | null>; visible: boolean } {
  const ref = useRef<HTMLDivElement | null>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true)
          obs.disconnect()
        }
      },
      { threshold },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, visible }
}

// ---------------------------------------------------------------------------
// Hero background — two LightRays instances from opposing corners
// ---------------------------------------------------------------------------
function HeroLightRays() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {/* Primary: top-left, violet */}
      <div className="absolute inset-0" style={{ mixBlendMode: "screen", opacity: 0.45 }}>
        <LightRays
          raysOrigin="top-left"
          raysColor="#dfdfdf"
          raysSpeed={0.35}
          lightSpread={2.8}
          rayLength={5}
          fadeDistance={0.9}
          followMouse={true}
          mouseInfluence={0.07}
          pulsating={false}
          noiseAmount={0.2}
        />
      </div>
      {/* Secondary: top-right, indigo — slower, wider, pulsating */}
      <div className="absolute inset-0" style={{ mixBlendMode: "screen", opacity: 0.45 }}>
        <LightRays
          raysOrigin="top-right"
          raysColor="#e8e8e8"
          raysSpeed={0.25}
          lightSpread={2.2}
          rayLength={4}
          fadeDistance={1.0}
          followMouse={true}
          mouseInfluence={0.07}
          pulsating={true}
          noiseAmount={0.2}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Browser chrome wrapper
// ---------------------------------------------------------------------------
function BrowserFrame({
  children,
  className,
  route,
}: {
  children: React.ReactNode
  className?: string
  route?: string
}) {
  return (
    <div
      className={cn(
        "rounded-xl overflow-hidden border border-border shadow-2xl shadow-black/10 dark:shadow-black/40",
        className,
      )}
    >
      <div className="h-9 bg-muted/60 border-b border-border flex items-center gap-2 px-3 shrink-0">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400/50" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/50" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/50" />
        </div>
        <div className="mx-2 h-5 rounded-md bg-background/50 flex-1 max-w-[180px] flex items-center px-2">
          <span className="text-[9px] text-muted-foreground truncate">
            mimir.online{route ? `/${route}` : ""}
          </span>
        </div>
      </div>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sidebar — used in Palace and Chat mockups
// ---------------------------------------------------------------------------
function MockSidebar({ active, className }: { active: "palace" | "chat" | "notes"; className?: string }) {
  const items = [
    { id: "home", label: "Home" },
    { id: "palace", label: "Mind Palace" },
    { id: "chat", label: "Chat" },
    { id: "notes", label: "Notes" },
  ] as const

  return (
    <div className={cn("w-44 border-r border-border bg-muted/20 p-3 flex flex-col gap-0.5 shrink-0", className)}>
      <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
        <div className="w-4 h-4 rounded bg-foreground/20 shrink-0" />
        <span className="text-[11px] font-semibold text-foreground/70">Mímir</span>
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(
            "flex items-center gap-2 px-2 py-1.5 rounded-lg",
            item.id === active ? "bg-primary/10 text-foreground" : "text-muted-foreground",
          )}
        >
          <div
            className={cn(
              "w-3.5 h-3.5 rounded-sm shrink-0",
              item.id === active ? "bg-primary/60" : "bg-border",
            )}
          />
          <span
            className={cn(
              "text-[11px] font-medium",
              item.id === active ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {item.label}
          </span>
        </div>
      ))}
      <div className="mt-4 px-2">
        <p className="text-[9px] text-muted-foreground/60 uppercase tracking-widest font-semibold mb-1.5">
          Tags
        </p>
        {["research", "reading", "work"].map((t) => (
          <div key={t} className="flex items-center gap-1.5 py-1">
            <div className="w-2 h-2 rounded-full bg-primary/30" />
            <span className="text-[10px] text-muted-foreground">{t}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Mind Palace
// ---------------------------------------------------------------------------
const PALACE_CARDS = [
  {
    type: "LINK",
    color: "text-blue-500",
    badge: "bg-blue-500/15",
    title: "The Feynman Technique",
    summary: "Learning by teaching — master any concept by explaining it simply.",
    tag: "research",
  },
  {
    type: "FILE",
    color: "text-amber-500",
    badge: "bg-amber-500/15",
    title: "Q4 Research Notes",
    summary: "Key findings from the market analysis and competitor landscape.",
    tag: "work",
  },
  {
    type: "TEXT",
    color: "text-emerald-500",
    badge: "bg-emerald-500/15",
    title: "On Writing Well",
    summary: "Strunk & White's core principles: clarity, brevity, humanity.",
    tag: "reading",
  },
  {
    type: "LINK",
    color: "text-purple-500",
    badge: "bg-purple-500/15",
    title: "Attention Is All You Need",
    summary: "The original transformer paper — self-attention mechanism.",
    tag: "research",
  },
  {
    type: "FILE",
    color: "text-rose-500",
    badge: "bg-rose-500/15",
    title: "Personal OKRs 2025",
    summary: "Objectives and key results across health, work, and learning.",
    tag: "work",
  },
  {
    type: "TEXT",
    color: "text-indigo-500",
    badge: "bg-indigo-500/15",
    title: "Second Brain Method",
    summary: "Tiago Forte's CODE framework — Capture, Organise, Distil, Express.",
    tag: "reading",
  },
]

function PalaceMockup() {
  return (
    <BrowserFrame route="mind-palace">
      <div className="flex bg-background" style={{ height: 340 }}>
        <MockSidebar active="palace" className="hidden sm:flex" />
        <div className="flex-1 p-4 overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-foreground">Mind Palace</h3>
            <div className="flex gap-1.5">
              <div className="h-6 px-2 rounded-md bg-muted text-[9px] text-muted-foreground flex items-center">
                All
              </div>
              <div className="h-6 px-2 rounded-md bg-muted text-[9px] text-muted-foreground flex items-center">
                Links
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {PALACE_CARDS.map((c) => (
              <div
                key={c.title}
                className="rounded-xl border border-border bg-card p-3 flex flex-col gap-1.5"
              >
                <span className={cn("text-[8px] font-bold tracking-widest uppercase", c.color)}>
                  {c.type}
                </span>
                <p className="text-[10px] font-semibold leading-tight text-foreground line-clamp-2">
                  {c.title}
                </p>
                <p className="text-[9px] text-muted-foreground leading-snug line-clamp-2">
                  {c.summary}
                </p>
                <span
                  className={cn(
                    "mt-auto text-[8px] px-1.5 py-0.5 rounded-full font-medium w-fit",
                    c.badge,
                    c.color,
                  )}
                >
                  {c.tag}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Chat
// ---------------------------------------------------------------------------
function ChatMockup() {
  return (
    <BrowserFrame route="chat">
      <div className="flex bg-background" style={{ height: 340 }}>
        <div className="w-44 border-r border-border bg-muted/20 p-3 hidden sm:flex flex-col gap-1 shrink-0">
          <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
            <div className="w-4 h-4 rounded bg-foreground/20 shrink-0" />
            <span className="text-[11px] font-semibold text-foreground/70">Mímir</span>
          </div>
          <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-primary/10 text-left mb-1">
            <div className="w-3.5 h-3.5 rounded-sm bg-primary/60 shrink-0" />
            <span className="text-[10px] font-medium text-foreground">Chat</span>
          </button>
          <p className="text-[9px] text-muted-foreground/60 uppercase tracking-widest font-semibold px-2 mb-0.5">
            Recents
          </p>
          {["What's the Feynman Technique?", "Summarize my Q4 notes", "Best books on writing"].map(
            (t) => (
              <div key={t} className="px-2 py-1.5 rounded-lg hover:bg-muted/50">
                <p className="text-[10px] text-muted-foreground line-clamp-1">{t}</p>
              </div>
            ),
          )}
        </div>
        <div className="flex-1 flex flex-col p-4 gap-3 overflow-hidden">
          <div className="flex justify-end">
            <div className="max-w-[65%] bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-3 py-2">
              <p className="text-[11px] leading-snug">
                What is the Feynman Technique and how can I use it to learn faster?
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="max-w-[88%] bg-muted rounded-2xl rounded-bl-sm px-3 py-2.5">
              <p className="text-[11px] leading-snug text-foreground">
                The Feynman Technique is a four-step method: choose a concept, explain it simply,
                find your gaps, then simplify further. The power is in the{" "}
                <span className="font-semibold">simplicity test</span> — if you can't explain it
                plainly, you don't fully understand it yet.
              </p>
            </div>
            <div className="flex items-center gap-2 ml-2">
              <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider font-medium">
                From Palace
              </span>
              <span className="h-4 px-1.5 rounded-md bg-blue-500/10 text-blue-500 text-[9px] font-medium flex items-center">
                The Feynman Technique
              </span>
            </div>
          </div>
          <div className="mt-auto h-8 rounded-xl border border-border bg-muted/30 flex items-center px-3 gap-2">
            <span className="text-[10px] text-muted-foreground/50">Ask Mimir anything…</span>
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Notes
// ---------------------------------------------------------------------------
const NOTE_ITEMS = [
  {
    bg: "bg-amber-100 dark:bg-amber-900/40",
    border: "border-amber-200 dark:border-amber-700/40",
    text: "Learn more about spaced repetition — look into Anki scheduling algorithm.",
  },
  {
    bg: "bg-blue-100 dark:bg-blue-900/40",
    border: "border-blue-200 dark:border-blue-700/40",
    text: "Book rec from Julia: 'The Creative Act' by Rick Rubin. Add to reading list.",
  },
  {
    bg: "bg-emerald-100 dark:bg-emerald-900/40",
    border: "border-emerald-200 dark:border-emerald-700/40",
    text: '"Clarity is kindness." — perfect quote for the team presentation intro.',
  },
  {
    bg: "bg-rose-100 dark:bg-rose-900/40",
    border: "border-rose-200 dark:border-rose-700/40",
    text: "Follow up with Marco on the API integration decision by Friday.",
  },
  {
    bg: "bg-purple-100 dark:bg-purple-900/40",
    border: "border-purple-200 dark:border-purple-700/40",
    text: "Idea: build a weekly digest of saved links — could be automated with Mimir.",
  },
  {
    bg: "bg-zinc-100 dark:bg-zinc-800/60",
    border: "border-zinc-200 dark:border-zinc-700/40",
    text: "Morning routine: 20 min reading → 10 min notes → 5 min review.",
  },
]

function NotesMockup() {
  return (
    <BrowserFrame route="notes">
      <div className="bg-background p-4" style={{ height: 340 }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-foreground">Notes</h3>
          <div className="h-6 px-2 rounded-md bg-primary/10 text-primary text-[9px] font-medium flex items-center">
            + New note
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {NOTE_ITEMS.map((n, i) => (
            <div
              key={i}
              className={cn("rounded-xl border p-3", n.bg, n.border)}
              style={{ minHeight: 80 }}
            >
              <p className="text-[10px] leading-snug text-foreground/80 line-clamp-4">{n.text}</p>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ---------------------------------------------------------------------------
// Feature row — alternating
// ---------------------------------------------------------------------------
function FeatureRow({
  eyebrow,
  headline,
  body,
  mockup,
  flip = false,
}: {
  eyebrow: string
  headline: string
  body: string
  mockup: React.ReactNode
  flip?: boolean
}) {
  const { ref, visible } = useReveal()
  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col lg:flex-row items-center gap-12 lg:gap-16 transition-all duration-700",
        flip && "lg:flex-row-reverse",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
      )}
    >
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        <span className="text-xs font-semibold tracking-widest uppercase text-primary">
          {eyebrow}
        </span>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">{headline}</h2>
        <p className="text-lg text-muted-foreground leading-relaxed">{body}</p>
      </div>
      <div className="flex-1 w-full min-w-0 overflow-hidden">{mockup}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------
function Landing() {
  const { isSignedIn, isLoaded } = useAuth()
  const navigate = useNavigate()
  const { ref: ctaRef, visible: ctaVisible } = useReveal()

  useEffect(() => {
    if (isLoaded && isSignedIn) navigate({ to: "/home" })
  }, [isLoaded, isSignedIn, navigate])

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <HeroLightRays />
      {/* Navbar */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-10 py-3.5 bg-background/60 backdrop-blur-md border-b border-border/50">
        <MimirLogo className="h-7" />
        {isLoaded && !isSignedIn && (
          <SignInButton mode="modal">
            <Button variant="outline" size="sm" className="px-6">
              Sign in
            </Button>
          </SignInButton>
        )}
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden pt-24 pb-0 px-6 flex flex-col items-center text-center">
        {/* Fade out shader at bottom */}
        <div
          className="absolute bottom-0 inset-x-0 h-48 bg-linear-to-b from-transparent to-background pointer-events-none z-10"
          aria-hidden
        />

        <div className="relative z-20 flex flex-col items-center gap-6 max-w-3xl">
          <MimirLogo iconOnly className="h-24 text-foreground/80" />
          <p className="text-xs font-semibold tracking-widest uppercase text-primary">
            Now in open beta
          </p>
          <h1
            className="font-bold tracking-tight leading-[1.08] text-foreground"
            style={{ fontSize: "clamp(2.5rem, 7vw, 5rem)" }}
          >
            Your second brain
            <br />
            powered by AI
          </h1>
          <p className="text-xl text-muted-foreground max-w-lg leading-relaxed">
            Save anything you read. Ask anything you wonder. Mimir keeps your knowledge connected
            and always within reach.
          </p>
          {isLoaded && !isSignedIn && (
            <div className="flex flex-col items-center gap-3 w-full max-w-sm mt-2">
              <SignUpButton mode="modal">
                <Button size="lg" className="w-full">
                  Get started - it's free
                </Button>
              </SignUpButton>
              <p className="text-xs text-muted-foreground">
                Already have an account?{" "}
                <SignInButton mode="modal">
                  <button className="underline underline-offset-2 hover:text-foreground transition-colors">
                    Sign in
                  </button>
                </SignInButton>
              </p>
            </div>
          )}
        </div>

        {/* Hero app mockup with caption */}
        <div className="relative z-20 w-full max-w-5xl mt-16 mb-5">
          <div className="p-px rounded-2xl bg-linear-to-b from-border/80 to-transparent overflow-hidden">
            <PalaceMockup />
          </div>
          <p className="mt-4 pb-16 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Mind Palace</span> — everything you've
            ever saved, semantically indexed and always searchable
          </p>
        </div>
      </section>

      {/* Feature rows */}
      <div className="flex flex-col gap-32 py-32 px-6 md:px-10 max-w-6xl mx-auto">
        <FeatureRow
          eyebrow="Mind Palace"
          headline="Everything you've ever saved, instantly findable"
          body="Capture articles, PDFs, research papers, and notes. Mimir indexes them semantically — not just by keyword — so you can find exactly what you need even when you've forgotten the words."
          mockup={<PalaceMockup />}
        />
        <FeatureRow
          flip
          eyebrow="Mimir Chat"
          headline="Ask anything. Get answers grounded in your knowledge."
          body="Mimir cites its sources directly from your saved documents. Ask follow-up questions, explore connections, and let your past reading do the heavy lifting."
          mockup={<ChatMockup />}
        />
        <FeatureRow
          eyebrow="Quick Notes"
          headline="Capture thoughts before they slip away"
          body="Notes that live alongside your documents. Search across all of them, and let Mimir connect your fleeting ideas to your deeper knowledge base."
          mockup={<NotesMockup />}
        />
      </div>

      {/* Final CTA */}
      <section
        ref={ctaRef}
        className={cn(
          "py-32 px-6 text-center transition-all duration-700",
          ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
        )}
      >
        <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">
          Get started today
        </p>
        <h2
          className="font-bold tracking-tight mb-4 leading-tight"
          style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
        >
          Build your Mind Palace.
        </h2>
        <p className="text-lg text-muted-foreground mb-10 max-w-md mx-auto">
          Sign up free and start building your second brain today.
        </p>
        {isLoaded && !isSignedIn && (
          <div className="flex flex-col items-center max-w-sm mx-auto">
            <SignUpButton mode="modal">
              <Button size="lg" className="w-full">
                Get started - it's free
              </Button>
            </SignUpButton>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8 px-6 md:px-10 flex items-center justify-between">
        <MimirLogo className="h-5" />
        <p className="text-xs text-muted-foreground">Your knowledge, your palace.</p>
      </footer>
    </div>
  )
}
